import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PayrollService } from '@/services/payroll.service';
import { HRService } from '@/services/hr.service';
import {
  DeductionType,
  DeductionStatus,
} from '@/models/payroll.model';
import { format } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';

export const Deductions: React.FC = () => {
  const { profile } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterStatus, setFilterStatus] = useState<DeductionStatus | 'all'>(
    (searchParams.get('status') as DeductionStatus) || 'all'
  );
  const [filterType, setFilterType] = useState<DeductionType | 'all'>(
    (searchParams.get('type') as DeductionType) || 'all'
  );
  const [filterEmployee, setFilterEmployee] = useState<string>(
    searchParams.get('employeeId') || 'all'
  );
  const [searchTerm, setSearchTerm] = useState('');

  const queryClient = useQueryClient();

  const { data: deductions = [], isLoading } = useQuery({
    queryKey: ['deductions', filterStatus, filterType, filterEmployee],
    queryFn: async () => {
      if (filterEmployee !== 'all') {
        return await PayrollService.getDeductionsByEmployee(filterEmployee, filterStatus === 'all');
      }
      const all = await PayrollService.getAllDeductions();
      return all.filter((d) => {
        if (filterStatus !== 'all' && d.status !== filterStatus) return false;
        if (filterType !== 'all' && d.type !== filterType) return false;
        return true;
      });
    },
    enabled: !!profile,
  });

  const { data: employees = [] } = useQuery({
    queryKey: ['employees'],
    queryFn: () => HRService.getAllEmployees(),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await PayrollService.deleteDeduction(id, profile?.id || '');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deductions'] });
    },
  });

  const filteredDeductions = deductions.filter((d) => {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        d.name.toLowerCase().includes(searchLower) ||
        d.employeeName.toLowerCase().includes(searchLower) ||
        d.description?.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  // Calculate statistics
  const stats = {
    total: filteredDeductions.length,
    active: filteredDeductions.filter((d) => d.status === DeductionStatus.active).length,
    preTax: filteredDeductions.filter((d) => d.type === DeductionType.preTax).length,
    postTax: filteredDeductions.filter((d) => d.type === DeductionType.postTax).length,
    garnishments: filteredDeductions.filter((d) => d.type === DeductionType.garnishment).length,
    totalYTD: filteredDeductions.reduce((sum, d) => sum + (d.totalDeductedYTD || 0), 0),
  };

  const getStatusColor = (status: DeductionStatus) => {
    const colors: Record<DeductionStatus, string> = {
      [DeductionStatus.active]: 'bg-green-100 text-green-800',
      [DeductionStatus.inactive]: 'bg-gray-100 text-gray-800',
      [DeductionStatus.pending]: 'bg-yellow-100 text-yellow-800',
      [DeductionStatus.suspended]: 'bg-orange-100 text-orange-800',
      [DeductionStatus.terminated]: 'bg-red-100 text-red-800',
      [DeductionStatus.expired]: 'bg-gray-100 text-gray-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getTypeColor = (type: DeductionType) => {
    const colors: Record<DeductionType, string> = {
      [DeductionType.preTax]: 'bg-blue-100 text-blue-800',
      [DeductionType.postTax]: 'bg-purple-100 text-purple-800',
      [DeductionType.garnishment]: 'bg-red-100 text-red-800',
      [DeductionType.other]: 'bg-gray-100 text-gray-800',
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Deduction Management</h1>
            <p className="mt-2 text-gray-600">
              Manage employee deductions, garnishments, and voluntary contributions
            </p>
          </div>
          <Link
            to="/payroll/deductions/new"
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm font-medium"
          >
            + Add Deduction
          </Link>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Total Deductions</div>
          <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Active</div>
          <div className="text-2xl font-bold text-green-600">{stats.active}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Pre-Tax</div>
          <div className="text-2xl font-bold text-blue-600">{stats.preTax}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Post-Tax</div>
          <div className="text-2xl font-bold text-purple-600">{stats.postTax}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Garnishments</div>
          <div className="text-2xl font-bold text-red-600">{stats.garnishments}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Total YTD</div>
          <div className="text-2xl font-bold text-indigo-600">
            ${stats.totalYTD.toLocaleString()}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, employee..."
              className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value as DeductionStatus | 'all');
                setSearchParams({ ...Object.fromEntries(searchParams), status: e.target.value });
              }}
              className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Statuses</option>
              {Object.values(DeductionStatus).map((status) => (
                <option key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select
              value={filterType}
              onChange={(e) => {
                setFilterType(e.target.value as DeductionType | 'all');
                setSearchParams({ ...Object.fromEntries(searchParams), type: e.target.value });
              }}
              className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Types</option>
              {Object.values(DeductionType).map((type) => (
                <option key={type} value={type}>
                  {type.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Employee</label>
            <select
              value={filterEmployee}
              onChange={(e) => {
                setFilterEmployee(e.target.value);
                setSearchParams({
                  ...Object.fromEntries(searchParams),
                  employeeId: e.target.value,
                });
              }}
              className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Employees</option>
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.fullName}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Deductions List */}
      {isLoading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading deductions...</p>
        </div>
      ) : filteredDeductions.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-600">No deductions found</p>
          <Link
            to="/payroll/deductions/new"
            className="mt-4 inline-block bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm font-medium"
          >
            + Add Your First Deduction
          </Link>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Employee
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Deduction
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    YTD
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dates
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredDeductions.map((deduction) => (
                  <tr key={deduction.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {deduction.employeeName}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{deduction.name}</div>
                      {deduction.description && (
                        <div className="text-sm text-gray-500">{deduction.description}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeColor(
                          deduction.type
                        )}`}
                      >
                        {deduction.type.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {deduction.percentage
                          ? `${deduction.percentage}%`
                          : `$${deduction.amount?.toFixed(2) || '0.00'}`}
                      </div>
                      {deduction.calculationMethod !== 'fixed_amount' && (
                        <div className="text-xs text-gray-500">
                          {deduction.calculationMethod.replace('_', ' ')}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                          deduction.status
                        )}`}
                      >
                        {deduction.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${(deduction.totalDeductedYTD || 0).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div>Start: {format(deduction.startDate, 'MMM d, yyyy')}</div>
                      {deduction.endDate && (
                        <div>End: {format(deduction.endDate, 'MMM d, yyyy')}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <Link
                        to={`/payroll/deductions/${deduction.id}`}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                      >
                        View
                      </Link>
                      <Link
                        to={`/payroll/deductions/${deduction.id}/edit`}
                        className="text-indigo-600 hover:text-indigo-900 mr-4"
                      >
                        Edit
                      </Link>
                      {deduction.status === DeductionStatus.active && (
                        <button
                          onClick={() => {
                            if (confirm('Are you sure you want to terminate this deduction?')) {
                                deleteMutation.mutate(deduction.id);
                              }
                            }}
                          className="text-red-600 hover:text-red-900"
                        >
                          Terminate
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

